import {
  FETCH_SOURCES,
  FILE_IMPORT_LIMIT,
  LEVELING_BOTS,
  levelingConfigSchema,
} from "@colossus/contracts";
import type { Formula } from "@colossus/contracts";
import type { RoleEntity } from "./entities.ts";
import type {
  FetchSource,
  FileImportBody,
  ImportRun,
  LevelingMember,
  LevelingTransfer,
  ReviewFilter,
} from "./leveling.ts";
import { importSourceLabel } from "./import-source.ts";
import { aboveColossus, ladderUnheld } from "./leveling-rewards.ts";
import type { ColossusReach, RewardDraft } from "./leveling-rewards.ts";
import { m } from "./paraglide/messages.js";

export type RunPhase = "reading" | "staged" | "counted" | "empty";

export const runPhase = (run: Pick<ImportRun, "status" | "committedAt" | "imported">): RunPhase => {
  if (run.status === "running") return "reading";
  if (run.committedAt !== null) return "counted";
  return run.imported === 0 ? "empty" : "staged";
};

export const runSubject = (run: Pick<ImportRun, "source" | "bot" | "botId">): string => {
  switch (run.source) {
    case "messages":
      return m.leveling_run_subject_messages({
        bot:
          run.botId === null
            ? m.leveling_switch_other_bot()
            : (LEVELING_BOTS.get(run.botId) ?? run.botId),
      });
    case "file":
      return run.bot === null
        ? m.leveling_run_subject_file()
        : m.leveling_run_subject_file_bot({ bot: importSourceLabel(run.bot) });
    case "roles":
      return m.leveling_run_subject_roles();
    case "claims":
      return m.leveling_run_subject_claims();
    default:
      return m.leveling_run_subject_board({ bot: importSourceLabel(run.source) });
  }
};

export const runProgress = (
  run: Pick<ImportRun, "total" | "imported" | "skipped" | "belowMin">,
): number | null =>
  run.total === null || run.total <= 0
    ? null
    : Math.min(1, (run.imported + run.skipped + run.belowMin) / run.total);

export type BringWay = "board" | "file" | "roles" | "claims";

const BRING_WAYS = {
  mee6: ["board"],
  lurkr: ["board", "file"],
  amari: ["board"],
  kiai: ["board", "file"],
  tatsu: ["board"],
  juniper: ["board"],
  roles: ["roles"],
  claims: ["claims"],
} as const satisfies Record<string, readonly [BringWay] | readonly [BringWay, BringWay]>;

export type BringSource = keyof typeof BRING_WAYS;

export type BringBot = Exclude<BringSource, "roles" | "claims">;

export const BRING_BOTS = Object.keys(BRING_WAYS).filter(
  (source) => source !== "roles" && source !== "claims",
) as readonly BringBot[];

const squashed = (name: string): string => name.toLowerCase().replace(/[^a-z0-9]/g, "");

export const bringBotOf = (name: string): BringBot | null => {
  return BRING_BOTS.find((bot) => squashed(name).startsWith(bot)) ?? null;
};

export interface ImportPick {
  readonly id: string;
  readonly name: string;
  readonly source: BringSource;
}

export const importSources = (
  present: readonly { readonly botId: string; readonly name: string }[] = [],
): readonly ImportPick[] => {
  const held = new Set(present.map((bot) => bot.botId));
  const bots = [
    ...present,
    ...[...LEVELING_BOTS.entries()].flatMap(([botId, name]) =>
      held.has(botId) ? [] : [{ botId, name }],
    ),
  ].flatMap(({ botId, name }): readonly ImportPick[] => {
    const source = bringBotOf(LEVELING_BOTS.get(botId) ?? name);
    return source === null ? [] : [{ id: botId, name, source }];
  });
  return [
    ...bots,
    { id: "roles", name: m.leveling_bring_roles(), source: "roles" },
    { id: "claims", name: m.leveling_claims_mode(), source: "claims" },
  ];
};

export const boardOf = (bot: BringSource): FetchSource | null => {
  const found = FETCH_SOURCES.find((source) => source === bot);
  return found === undefined ? null : found;
};

interface BringFacts {
  readonly fetchable: readonly FetchSource[];
  readonly held: boolean;
  readonly claimsOff: boolean;
}

const followOpen = (
  way: "roles" | "claims",
  facts: Pick<BringFacts, "held" | "claimsOff">,
): boolean => (way === "roles" ? facts.held : facts.claimsOff);

const wayOpen = (bot: BringSource, way: BringWay, facts: BringFacts): boolean => {
  switch (way) {
    case "board": {
      const source = boardOf(bot);
      return source !== null && facts.fetchable.includes(source);
    }
    case "roles":
    case "claims":
      return followOpen(way, facts);
    case "file":
      return true;
  }
};

export interface BringChoice {
  readonly way: BringWay;
  readonly open: boolean;
}

export const bringChoices = (bot: BringSource, facts: BringFacts): readonly BringChoice[] =>
  (BRING_WAYS[bot] as readonly BringWay[]).map((way) => ({ way, open: wayOpen(bot, way, facts) }));

export const firstOpen = (choices: readonly BringChoice[]): BringWay | null => {
  const found = choices.find((choice) => choice.open);
  return found === undefined ? null : found.way;
};

export const missingWays = (
  used: BringWay | null,
  facts: Pick<BringFacts, "held" | "claimsOff">,
): readonly ("roles" | "claims")[] =>
  (["roles", "claims"] as const).filter((way) => way !== used && followOpen(way, facts));

type FileRow = FileImportBody["rows"][number];
type FileHead = Omit<FileImportBody, "rows" | "runId">;

export type PartSent =
  | { readonly ok: true; readonly run: Pick<ImportRun, "runId" | "imported" | "skipped"> }
  | { readonly ok: false; readonly refusal: string };

interface FileSent {
  readonly runId: string | null;
  readonly sent: number;
  readonly imported: number;
  readonly skipped: number;
  readonly refusal: string | null;
}

export const sendFileInParts = async (
  rows: readonly FileRow[],
  head: FileHead,
  send: (body: FileImportBody) => Promise<PartSent>,
  limit: number = FILE_IMPORT_LIMIT,
): Promise<FileSent> => {
  let runId: string | null = null;
  let sent = 0;
  let imported = 0;
  let skipped = 0;
  while (sent < rows.length) {
    const part = rows.slice(sent, sent + limit).map((row) => ({ ...row }));
    const answer = await send({ ...head, ...(runId === null ? {} : { runId }), rows: part });
    if (!answer.ok) return { runId, sent, imported, skipped, refusal: answer.refusal };
    runId = answer.run.runId;
    imported = answer.run.imported;
    skipped = answer.run.skipped;
    sent += part.length;
  }
  return { runId, sent, imported, skipped, refusal: null };
};

export const REVIEW_FILTERS = [
  "all",
  "rising",
  "falling",
  "same",
  "unseen",
  "departed",
] as const satisfies readonly ReviewFilter[];

export const paceFormula = (pace: LevelingTransfer["pace"]): Formula | null => {
  if (pace === null) return null;
  const parsed = levelingConfigSchema.shape.formula.safeParse(pace.formula);
  return parsed.success ? parsed.data : null;
};

interface Provenance {
  readonly source: string;
  readonly level: number;
  readonly observedAt: string;
  readonly link: string | null;
  readonly earned: number;
}

export const discordMessageUrl = (guildId: string, channelId: string, messageId: string): string =>
  `https://discord.com/channels/${guildId}/${channelId}/${messageId}`;

const standingOf = (entry: LevelingMember["imports"][number]): number =>
  entry.progress === null ? entry.level : entry.level + entry.progress;

export const provenance = (
  member: Pick<LevelingMember, "xp" | "imports" | "imported" | "adjusted">,
  guildId: string,
): Provenance | null => {
  if (member.imported <= 0) return null;
  const best = member.imports
    .filter((entry) => entry.committed)
    .reduce<LevelingMember["imports"][number] | null>((top, entry) => {
      if (top === null) return entry;
      return standingOf(entry) > standingOf(top) ? entry : top;
    }, null);
  if (best === null) return null;
  return {
    source: best.source,
    level: best.level,
    observedAt: best.observedAt,
    link:
      best.messageId === null || best.channelId === null
        ? null
        : discordMessageUrl(guildId, best.channelId, best.messageId),
    earned: Math.max(0, member.xp - member.imported - member.adjusted),
  };
};

interface LadderReadiness {
  readonly rewards: number;
  readonly above: number;
  readonly unheld: number;
  readonly replacing: boolean;
}

export const ladderReadiness = (facts: {
  readonly rewards: readonly RewardDraft[];
  readonly roles: readonly RoleEntity[];
  readonly colossus: ColossusReach | null;
}): LadderReadiness => {
  const { rewards, roles, colossus } = facts;
  const carried = new Set(rewards.flatMap((item) => item.carries));
  const named = new Set(
    rewards.flatMap((item) => {
      if (item.grant.kind === "role") return [item.grant.roleId];
      return item.grant.role.mode === "adopt" && item.grant.role.roleId !== null
        ? [item.grant.role.roleId]
        : [];
    }),
  );
  return {
    rewards: rewards.length,
    above:
      colossus === null
        ? 0
        : roles.filter((role) => named.has(role.id) && aboveColossus(role, colossus)).length,
    unheld: colossus === null ? 0 : ladderUnheld(rewards, colossus).length,
    replacing: rewards.some(
      (item) => item.trigger.kind === "level" && item.grant.kind === "role" && carried.has(item.id),
    ),
  };
};
